import { useMemo } from 'react';
import { GET_PLATFORM_USERS_WITHOUT_RELATIONS_QUERY, usePlatformUsers } from 'src/graphql';

import { ConditionInput, SortingInput } from 'src/graphql/types';

import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const DEFAULT_FILTER: ConditionInput[] = [];

export const DEFAULT_SEARCH_PLATFORM_USERS_TARGET_IDS = ['platformUser/userId'];

export const useSearchPlatformUsers = (params: {
  searchTerm: string | null;
  targetIdAttributes?: string[];
  filter?: ConditionInput[];
  limit?: number;
  sort?: SortingInput | SortingInput[];
  skip?: boolean;
  showResultsWhenEmptySearchTerm?: boolean;
  shouldIncludePlatformPartnerId?: boolean;
  partnerId?: number | null;
}) => {
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const {
    filter = DEFAULT_FILTER,
    skip,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    limit,
    shouldIncludePlatformPartnerId,
    partnerId,
  } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: targetIdAttributes ?? DEFAULT_SEARCH_PLATFORM_USERS_TARGET_IDS,
        }
      : undefined;

    return {
      filters: filter,
      sort,
      limit,
      offset: 0,
      searchParams,
      partnerId,
      shouldIncludePlatformPartnerId,
    };
  }, [
    filter,
    limit,
    partnerId,
    searchTerm,
    shouldIncludePlatformPartnerId,
    sort,
    targetIdAttributes,
  ]);

  const { platformUsers, loading, ...rest } = usePlatformUsers(queryOptions, {
    skip: skip || (!showResultsWhenEmptySearchTerm && !searchTerm),
  });

  return {
    ...rest,
    isLoading: loading,
    records: platformUsers,
  };
};

export const useSearchPlatformUsersOptions = (params: {
  searchTerm: string;
  limit?: number;
  skip?: boolean;
  shouldIncludePlatformPartnerId?: boolean;
  partnerId?: number | null;
}) => {
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const { skip, limit, shouldIncludePlatformPartnerId, partnerId } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: DEFAULT_SEARCH_PLATFORM_USERS_TARGET_IDS,
        }
      : undefined;

    return {
      limit,
      offset: 0,
      searchParams,
      partnerId,
      shouldIncludePlatformPartnerId,
      query: GET_PLATFORM_USERS_WITHOUT_RELATIONS_QUERY,
      filters: [
        <ConditionInput>{
          attribute: 'isActive',
          operator: '=',
          value: 'true',
        },
      ],
    };
  }, [limit, partnerId, searchTerm, shouldIncludePlatformPartnerId]);

  const { platformUsers, loading, ...rest } = usePlatformUsers(queryOptions, {
    skip,
  });

  return {
    ...rest,
    isLoading: loading,
    records: platformUsers,
  };
};
